import type { Pet, PetAttribute, PetEvolutionStage, PetStatusEffect } from '.';

import { assertNever, clamp } from '../../utils';
import { duration } from '../../utils/duration';
import { evolutions, PetEvolution, SpeciesID } from './pet.ruleset';
import { weightedRandom } from 'src/replicant/utils/random';
import { createPrng } from '@play-co/replicant';
import * as ruleset from './pet.ruleset';

export const petGetters = (
  Pet: Pet,
  { Fate }: Pet['$DEPENDENCIES'],
  features: (state: Pet.State) => { fate: boolean },
) => {
  const hasFate = (state: Pet.State) =>
    features(state).fate && !!state.fate.seed;

  /**
   * Get pet age, one real day == one year of pet age
   */
  const getPetAge = (state: Pet.State, now: number) => {
    const isDead = getPetState(state, now, false) === 'dead';

    const since = isDead
      ? Fate.api.getPermanentStatusEffectStatTime(state, 'dead', now)
      : now;

    return (
      Math.floor((since - getPetCreatedAt(state)) / duration({ days: 1 })) || 0
    );
  };

  const getAttributeValues = (state: Pet.State, now: number) =>
    Object.keys(state.pet.attributes).reduce(
      (values, attribute) => ({
        ...values,
        [attribute]: getAttributeValue(state, attribute as PetAttribute, now),
      }),
      {} as Record<PetAttribute, number>,
    );

  const getStressValue = (state: Pet.State, now: number) =>
    getPetCreatedAt(state)
      ? Pet.ruleset.getStressValue(getAttributeValues(state, now))
      : 0;

  const shouldGetSick = (state: Pet.State, random: number, now: number) =>
    getPetCreatedAt(state) &&
    Pet.ruleset.shouldGetSick(getStressValue(state, now), random);

  const shouldGetToothDecay = (state: Pet.State, random: number, now: number) =>
    getPetCreatedAt(state) &&
    Pet.ruleset.shouldGetToothDecay(
      getAttributeValue(state, 'toothAcidity', now),
      random,
    );

  const shouldGetScared = (state: Pet.State, random: number, now: number) =>
    Pet.ruleset.shouldGetScared(
      getCurrentEvolution(state)?.species,
      {
        current: getAttributeValue(state, 'love', now),
        max: Pet.ruleset.attributeBounds.max,
      },
      random,
    );

  const shouldWantToGoOut = (state: Pet.State, random: number, now: number) => {
    // Feature not unlocked yet.
    const { VisitRecord } = Pet.$DEPENDENCIES;
    if (!VisitRecord.getters.isUnlocked(state)) {
      return false;
    }

    const lastVisitTimestamp = VisitRecord.getters.getLastVisitTimestamp(state);
    const timeSinceLastVisit = Pet.ruleset.getAwakeDuration(
      getCurrentEvolution(state),
      getPetCreatedAt(state),
      lastVisitTimestamp,
      now,
      state,
    );
    return Pet.ruleset.shouldWantToGoOut(timeSinceLastVisit, random);
  };

  const shouldBeAsleep = (state: Pet.State, now: number) => {
    return Pet.ruleset.shouldBeAsleep(getCurrentEvolution(state), now, state);
  };

  const shouldEvolve = (state: Pet.State, now: number) => {
    const lastEvolution = getCurrentEvolution(state);
    if (lastEvolution) {
      const { timestamp, stage } = lastEvolution;

      if (stage === 'youth') {
        return false;
      }

      if (now - timestamp >= Pet.ruleset.timeToEvolve[stage]) {
        return true;
      }
    }

    return false;
  };

  const hasStatusEffect = (
    state: Pet.State,
    effect: PetStatusEffect,
    now: number,
    since?: number,
  ) => {
    switch (effect) {
      case 'hadVisitor':
        return !!state.pet.statusEffects.hadVisitor?.value;

      case 'asleep':
        return shouldBeAsleep(state, now);

      case 'evolving':
        return shouldEvolve(state, now);

      // TODO: Remove it
      case 'dead':
      case 'dying':
        return false;

      default:
        return shouldGetStatusEffect(state, effect, now, since);
    }
  };

  const getNextStatusCheckTime = (
    lastUpdatedAt: number,
    petCreatedAt: number,
  ) => {
    const { statusEffectInterval } = Pet.ruleset;

    // Find duration between pet creation and last status effect update
    let lastUpdatedAtAge =
      Math.max(lastUpdatedAt ?? 0, petCreatedAt) - petCreatedAt;

    // Round down to nearest status check interval
    lastUpdatedAtAge =
      Math.floor(lastUpdatedAtAge / statusEffectInterval) *
      statusEffectInterval;

    // Return timestamp of next status check interval
    return petCreatedAt + lastUpdatedAtAge + statusEffectInterval;
  };

  const shouldGetStatusEffect = (
    state: Pet.State,
    effect: Exclude<
      PetStatusEffect,
      'evolving' | 'asleep' | 'hadVisitor' | 'dead' | 'dying'
    >,
    now: number,
    since?: number,
  ) => {
    const petCreatedAt = getPetCreatedAt(state);
    if (!petCreatedAt) {
      return false;
    }

    let checkStatus: (...args: [Pet.State, number, number]) => boolean;
    switch (effect) {
      case 'scared':
        checkStatus = shouldGetScared;
        break;
      case 'sick':
        checkStatus = shouldGetSick;
        break;
      case 'toothDecay':
        checkStatus = shouldGetToothDecay;
        break;
      case 'wantToGoOut':
        checkStatus = shouldWantToGoOut;
        break;
      default:
        assertNever(effect);
    }

    const {
      getTimeUntilAwake,
      statusEffectInterval,
      getAwakeDuration,
    } = Pet.ruleset;
    const statusEffect = state.pet.statusEffects[effect];

    // Compute future status effect values from next status check timestamp onwards
    let statusCheckTime = getNextStatusCheckTime(
      statusEffect.lastUpdatedAt,
      petCreatedAt,
    );

    // Create rng seed using player id, status effect and status check timestamp
    const seed = `${state.id}_${effect}#${statusCheckTime}`;
    let rngStartAt = 0;

    // Begin status checks from `since` parameter if present
    if (since > statusCheckTime) {
      const evolution = getCurrentEvolution(state);
      const petCreatedAt = getPetCreatedAt(state);
      const awakeDuration = getAwakeDuration(
        evolution,
        petCreatedAt,
        statusCheckTime,
        since,
        state,
      );
      rngStartAt = Math.ceil(awakeDuration / statusEffectInterval);
      statusCheckTime = getNextStatusCheckTime(since, petCreatedAt);
    }

    const rng = createPrng(seed, rngStartAt);

    let { value } = statusEffect;
    while (!value && statusCheckTime <= now) {
      if (shouldBeAsleep(state, statusCheckTime)) {
        const timeUntilAwake = getTimeUntilAwake(
          getCurrentEvolution(state),
          statusCheckTime,
          state,
        );
        statusCheckTime = getNextStatusCheckTime(
          statusCheckTime + timeUntilAwake,
          petCreatedAt,
        );
      } else {
        value = checkStatus(state, rng(), statusCheckTime);
        statusCheckTime += statusEffectInterval;
      }
    }

    return !!value;
  };

  const getAllStatusEffects = (state: Pet.State, now: number, since?: number) =>
    Object.keys(state.pet.statusEffects).reduce(
      (values, effect) => ({
        ...values,
        [effect]: hasStatusEffect(state, effect as PetStatusEffect, now, since),
      }),
      {} as Record<PetStatusEffect, boolean>,
    );

  const getPetState = (state: Pet.State, now: number, isVisiting: boolean) =>
    hasFate(state)
      ? Pet.ruleset.getPetStateFromFate(
          Fate.api.getCurrentPetState(state, now, isVisiting),
        )
      : getPetCreatedAt(state)
      ? Pet.ruleset.getPetState(
          getAttributeValues(state, now),
          getAllStatusEffects(state, now),
          isVisiting,
        )
      : undefined;

  return (state: Pet.State) => ({
    getPetState: (now: number, isVisiting: boolean) =>
      getPetState(state, now, isVisiting),

    getNextPetState: (now: number) => {
      if (hasFate(state)) {
        const nextFate = Fate.api.getNextPetState(state, now);
        if (!nextFate) {
          return null;
        }
        return {
          state: Pet.ruleset.getPetStateFromFate(nextFate.state),
          timestamp: nextFate.timestamp,
        };
      }
      const currentEvolution = getCurrentEvolution(state);
      const petState = getPetState(state, now, false);
      if (
        currentEvolution &&
        !Pet.ruleset.isTerminalState(currentEvolution.stage, petState)
      ) {
        let timestamp = now;

        const stateCopy = {
          ...state,
          pet: JSON.parse(JSON.stringify(state.pet)) as Pet.State['pet'],

          // explicitly copy WithMeta props because they're defined as non-enumerable
          // and don't get copied by spread operator
          id: state.id,
          createdAt: state.createdAt,
          updatedAt: state.updatedAt,
        };
        const statusEffects = getAllStatusEffects(stateCopy, timestamp);

        let nextState = petState;
        while (petState === nextState) {
          const since = timestamp;

          // Skip any asleep period so we don't burn compute for no reason
          const timeUntilAwake = Pet.ruleset.getTimeUntilAwake(
            currentEvolution,
            timestamp,
            state,
          );
          timestamp += timeUntilAwake || duration({ seconds: 1 });

          // Get attribute and status effect values for this period
          const attributes = getAttributeValues(stateCopy, timestamp);
          Object.entries(
            getAllStatusEffects(stateCopy, timestamp, since),
          ).forEach(([effect, val]) => {
            // If we don't already have this status effect,
            // update it with the value predicted for this period
            if (effect === 'asleep' || statusEffects[effect] === false) {
              statusEffects[effect] = val;
            }
          });

          // Determine pet state from predicted attributes / status effects
          nextState = Pet.ruleset.getPetState(attributes, statusEffects, false);
        }

        return { timestamp, state: nextState };
      }

      return null;
    },

    getAllAttributeValues: (now: number) => getAttributeValues(state, now),

    getAttributeValue: (attribute: PetAttribute, now: number) =>
      getAttributeValue(state, attribute, now),

    /**
     * Get next reborn species after death
     */
    getNextRebornSpecies(
      random: () => number,
    ): Extract<PetEvolution, { stage: 'baby' }> {
      // Get current evolution stage
      const { species } = getCurrentEvolution(state);

      // Get next possible evolutions weights and species
      const nextEvolutions: Partial<Record<SpeciesID, number>> =
        evolutions[species].reborn_weights;

      const ids = Object.keys(nextEvolutions) as SpeciesID[];

      // Transform data for weightedRandom
      const evolutionsArray = ids.map((item) => {
        return { chance: nextEvolutions[item], species: item };
      });

      const weights = evolutionsArray.map((item) => {
        return item.chance;
      });

      // Roll and detect species
      const index = weightedRandom(weights, random);
      const id = evolutionsArray[index].species;

      return evolutions[id] as Extract<PetEvolution, { stage: 'baby' }>;
    },

    getStressValue: (now: number) => getStressValue(state, now),

    getAllStatusEffects: (now: number) =>
      hasFate(state)
        ? Fate.api.getAllPermanentStatusEffects(state, now)
        : getAllStatusEffects(state, now),

    getResolutionAttempts: (statusEffect: PetStatusEffect) =>
      state.pet.statusEffects[statusEffect]?.resolutionAttempts,

    hasStatusEffect: (effect: PetStatusEffect, now: number) =>
      hasFate(state)
        ? Fate.api.hasPermanentStatusEffect(state, effect, now)
        : hasStatusEffect(state, effect, now),

    shouldGetSick: (random: number, now: number) =>
      hasFate(state)
        ? Fate.api.shouldGetSick(state, random, now, now)
        : shouldGetSick(state, random, now),

    shouldGetToothDecay: (random: number, now: number) =>
      hasFate(state)
        ? Fate.api.shouldGetToothDecay(state, random, now, now)
        : shouldGetToothDecay(state, random, now),

    shouldGetScared: (random: number, now: number) =>
      hasFate(state)
        ? Fate.api.shouldGetScared(state, random, now, now)
        : shouldGetScared(state, random, now),

    shouldBeAsleep: (now: number) =>
      hasFate(state)
        ? Fate.api.shouldBeAsleep(state, now)
        : shouldBeAsleep(state, now),

    shouldEvolve: (now: number) =>
      hasFate(state)
        ? Fate.api.shouldEvolve(state, now)
        : shouldEvolve(state, now),

    getCurrentEvolution: () => getCurrentEvolution(state),

    /**
     * Get pet age, one real day == one year of pet age
     */
    getPetAge: (now: number): number => getPetAge(state, now),

    /**
     * Get pet name
     */
    getPetName: () => state.pet.name,

    getPetCreatedAt: () => getPetCreatedAt(state),

    /**
     * Get next evolution species for pet
     */
    getNextSpeciesEvolution(
      random: () => number,
    ): { id: SpeciesID; stage: PetEvolutionStage } {
      // Get current evolution stage
      const { stage, species } = getCurrentEvolution(state);

      // Adults do not evolve
      if (stage === 'adult') return null;

      // Transform data for weightedRandom
      const evolutionsArray = getNextSpecies(species);
      const weights = evolutionsArray.map((item) => {
        return item.chance;
      });

      // Roll and detect species
      const index = weightedRandom(weights, random);
      const id = evolutionsArray[index].species;
      const resultStage = evolutions[id].stage;

      return { id, stage: resultStage };
    },

    getAnalyticsAttributeUserProperties: (now: number) =>
      getPetCreatedAt(state)
        ? {
            stomach: getAttributeValue(state, 'stomach', now),
            love: getAttributeValue(state, 'love', now),
            dirtiness: getAttributeValue(state, 'dirtiness', now),
            urine: getAttributeValue(state, 'urine', now),
            stress: getStressValue(state, now),
            tooth_acidity: getAttributeValue(state, 'toothAcidity', now),
          }
        : {},

    getAnalyticsUserProperties: (now: number, isVisiting: boolean) =>
      getPetCreatedAt(state)
        ? {
            pet_id: getCurrentEvolution(state).species,
            pet_state: getPetState(state, now, isVisiting),
            age: getPetAge(state, now),
          }
        : {},
  });
};

const getCurrentEvolution = ({ pet }: Pet.State) => [...pet.evolutions].pop();

const getPetCreatedAt = ({ pet }: Pet.State) => pet.evolutions[0]?.timestamp;

/**
 * Get array of next available evolutions
 *
 * @return [{ chance: number,  species: 'ball' | 'dog'... }, ...];
 */
function getNextSpecies(
  currentSpeciesID: SpeciesID,
): Array<{ chance: number; species: SpeciesID }> {
  // Get next possible evolutions weights and species
  const nextEvolutions: Partial<Record<SpeciesID, number>> =
    evolutions[currentSpeciesID].next_weights;

  const ids = Object.keys(nextEvolutions) as SpeciesID[];

  // Transform data for weightedRandom
  const evolutionsArray = ids.map((item) => {
    return { chance: nextEvolutions[item], species: item };
  });

  return evolutionsArray;
}

/**
 * Pet attributes interpolation
 **/
export function getAttributeValue(
  state: Pet.State,
  attribute: PetAttribute,
  now: number,
) {
  const currentEvolution = getCurrentEvolution(state);
  if (!currentEvolution) {
    return 0;
  }

  const {
    attributeBounds,
    decayIntervals,
    decayMultipliers,
    getAwakeDuration,
  } = ruleset;
  const petCreatedAt = getPetCreatedAt(state);
  const { species } = currentEvolution;
  const { value, lastUpdatedAt } = state.pet.attributes[attribute];
  const interval = getAwakeDuration(
    currentEvolution,
    petCreatedAt,
    lastUpdatedAt,
    now,
    state,
  );
  const decayAmount = interval / decayIntervals[species][attribute];
  const { max, min } = attributeBounds;
  return clamp(value + decayMultipliers[attribute] * decayAmount, min, max);
}
